/**
 * @typedef {import('firebase/compat/app').default.app.App | import('firebase-admin')} FirebaseClientOrAdmin
 */

/**
 * Retrieves the Firebase instance from the provided options or from global scope.
 *
 * @param {object} [options]
 * @param {FirebaseClientOrAdmin} [options.firebase] - The Firebase instance to use.
 * @returns {FirebaseClientOrAdmin} The Firebase instance.
 */
function getFirebase(options) {
  return options?.firebase ?? globalThis.FirebaseWorker ?? globalThis.firebase;
}

/**
 * Represents a company's locking settings for when a data transfer is in progress.
 * @typedef {object} TransferLocking
 * @property {boolean} lock_jobs - If true, all jobs for this company are locked
 * @property {boolean} lock_creating_users - If true, creating new users for this company is locked
 * @property {boolean} lock_models - If true, all models for this company are locked
 * @property {string} from_server_id - The server id that the data is being transferred from (e.g. 'katapult-production')
 * @property {string} from_domain - The domain that the data is being transferred from (e.g. 'katapultpro.com')
 * @property {string} to_server_id - The server id that the data is being transferred to (e.g. 'testing-katapult-pro')
 * @property {string} to_domain - The domain that the data is being transferred to (e.g. 'testing.katapultpro.com')
 */

/**
 * Listens to transfer locking changes for a specific company and invokes a callback with the updated data.
 *
 * @param {string} companyId - The ID of the company to listen for transfer locking changes.
 * @param {function} callback - The callback function to be invoked with the transfer locking data.
 * @param {object} [options]
 * @param {FirebaseClientOrAdmin} [options.firebase] - The Firebase app instance to use.
 * @returns {function} A function to disconnect the listener.
 */
export function listenToTransferLocking(companyId, callback, options) {
  const firebase = getFirebase(options);

  const transferLockingRef = firebase.database().ref(`transfer_locking/${companyId}`);
  const listener = transferLockingRef.on('value', (snapshot) => {
    const transferLocking = /** @type {TransferLocking|null} */ (snapshot.val());
    callback(transferLocking);
  });

  const disconnect = () => transferLockingRef.off('value', listener);
  return disconnect;
}

/**
 * Retrieves the transfer locking information for a given company.
 *
 * @param {string} companyId - The ID of the company to retrieve transfer locking information for.
 * @param {object} [options]
 * @param {FirebaseClientOrAdmin} [options.firebase] - The Firebase app instance to use.
 * @returns {Promise<TransferLocking|null>} A promise that resolves to the transfer locking information, or null if not found.
 */
export async function getTransferLocking(companyId, options) {
  const firebase = getFirebase(options);

  return (await firebase.database().ref(`transfer_locking/${companyId}`).once('value'))?.val();
}
